import React from 'react';
import styled from 'styled-components';
import colors from '../design/colors';
import fonts from '../design/typography';
import uiTokens from '../design/uiTokens';

interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  width?: string;
  height?: string;
  minHeight?: string;
  padding?: string;
  size: 'small' | 'medium' | 'large';
  text: React.ReactNode;
  buttonColor?: string;
  fontStyle?: React.CSSProperties;
  buttonHoverColor?: string;
  textColor?: string;
  textHoverColor?: string;
  disabledBackgroundColor?: string;
}

const Button = ({
  size, text, width, height, minHeight, padding, buttonColor,
  fontStyle, buttonHoverColor, textColor, textHoverColor,
  disabledBackgroundColor, type = 'button', ...buttonProps
}: IButtonProps) => (
  <ButtonContainer
    {...buttonProps}
    type={type}
    $size={size}
    $width={width}
    $height={height}
    $minHeight={minHeight}
    $padding={padding}
    $buttonColor={buttonColor}
    $hoverColor={buttonHoverColor}
    $textColor={textColor}
    $textHoverColor={textHoverColor}
    $disabledBackgroundColor={disabledBackgroundColor}
  >
    <ButtonText style={fontStyle}>{text}</ButtonText>
  </ButtonContainer>
);

export default Button;

const ButtonText = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  svg { flex-shrink: 0; }
`;

const ButtonContainer = styled.button<{
  $size: IButtonProps['size'];
  $width?: string;
  $height?: string;
  $minHeight?: string;
  $padding?: string;
  $buttonColor?: string;
  $hoverColor?: string;
  $textColor?: string;
  $textHoverColor?: string;
  $disabledBackgroundColor?: string;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height, $minHeight, $size }) => $height ?? $minHeight ?? uiTokens.controlHeight[$size]};
  min-height: ${({ $minHeight }) => $minHeight ?? '0'};
  padding: ${({ $padding, $size }) => $padding ?? ($size === 'small' ? '0 12px' : '0 16px')};
  border: 1px solid transparent;
  border-radius: ${uiTokens.radius.control};
  background-color: ${({ $buttonColor }) => $buttonColor ?? uiTokens.color.primary};
  color: ${({ $textColor }) => $textColor ?? colors.white};
  ${({ $size }) => ({ ...($size === 'small' ? fonts.label4Medium : $size === 'medium' ? fonts.label3Medium : fonts.body2Medium) })};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${({ $hoverColor, $buttonColor }) => $hoverColor ?? $buttonColor ?? uiTokens.color.primaryHover};
    color: ${({ $textHoverColor, $textColor }) => $textHoverColor ?? $textColor ?? colors.white};
  }
  &:focus-visible {
    outline: 3px solid ${colors.blue200};
    outline-offset: 2px;
  }
  &:disabled {
    background-color: ${({ $disabledBackgroundColor }) => $disabledBackgroundColor ?? colors.gray200};
    color: ${colors.gray500};
    cursor: not-allowed;
  }
`;
